import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Typography } from "@material-ui/core";
import { renderAsync } from "docx-preview";

/**
 * Renders a .docx document inline, in-browser, from a URL.
 *
 * The built-in PDFViewer only handles PDF/Excel, so the Document Repository's
 * static Quality documents (Word files bundled as assets) are rendered here with
 * docx-preview (fetch the file → render its HTML into a container div).
 */
const DocxPreview = ({ url, height = "75vh" }) => {
  const containerRef = useRef(null);
  const [status, setStatus] = useState("loading"); // loading | done | error

  useEffect(() => {
    let cancelled = false;
    const container = containerRef.current;
    if (!url || !container) {
      setStatus("error");
      return undefined;
    }
    setStatus("loading");
    container.innerHTML = "";
    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to fetch document (${res.status})`);
        return res.blob();
      })
      .then((blob) => {
        if (cancelled) return undefined;
        return renderAsync(blob, container, undefined, {
          className: "docx-preview",
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: false,
        });
      })
      .then(() => {
        if (!cancelled) setStatus("done");
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  return (
    <Box style={{ position: "relative", minHeight: 220 }}>
      {status === "loading" && (
        <Box style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 48 }}>
          <CircularProgress size={28} thickness={4} />
          <Typography style={{ marginTop: 12, color: "#888", fontSize: "0.85rem" }}>
            Loading document…
          </Typography>
        </Box>
      )}
      {status === "error" && (
        <Box style={{ padding: 48, textAlign: "center", color: "#c0392b" }}>
          Unable to render this document.
        </Box>
      )}
      <Box
        ref={containerRef}
        style={{
          maxHeight: height,
          overflowY: "auto",
          overflowX: "auto",
          background: "#fff",
          display: status === "done" ? "block" : "none",
        }}
      />
    </Box>
  );
};

export default DocxPreview;
